// dashboard/components/Wallet.jsx
import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  ArrowUpward,
  ArrowDownward,
  CreditCard,
  History,
} from '@mui/icons-material';

const Wallet = () => {
  const walletData = {
    balance: 12450.75,
    totalDeposits: 15200.00,
    totalWithdrawals: 2749.25,
  };

  const transactions = [
    { id: 1, type: 'deposit', amount: 500.00, date: '2024-01-15', status: 'completed', description: 'Bank Transfer' },
    { id: 2, type: 'withdrawal', amount: -200.00, date: '2024-01-12', status: 'pending', description: 'Withdrawal to Bank' },
    { id: 3, type: 'deposit', amount: 1000.00, date: '2024-01-10', status: 'completed', description: 'Credit Card' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Wallet
          </Typography>
          <Typography variant="body2" sx={{ color: '#8a8a9a' }}>
            Manage your funds and transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AttachMoney />}
            sx={{
              bgcolor: '#2ecc71',
              '&:hover': { bgcolor: '#27ae60' },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Deposit
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: '#eef2f7',
              color: '#1a1a2e',
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Withdraw
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Balance Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #eef2f7', background: 'linear-gradient(135deg, #1a1a2e 0%, #2a2a4e 100%)', color: '#fff' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                Total Balance
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                ${walletData.balance.toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Deposits</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2ecc71' }}>
                    ${walletData.totalDeposits.toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Withdrawals</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e74c3c' }}>
                    ${walletData.totalWithdrawals.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eef2f7', boxShadow: 'none', height: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e', mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {['Deposit', 'Withdraw', 'Transfer', 'History'].map((action, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: '#eef2f7',
                      color: '#1a1a2e',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#4a6cf7', bgcolor: 'rgba(74, 108, 247, 0.05)' },
                    }}
                  >
                    {action}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Transactions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #eef2f7', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                Recent Transactions
              </Typography>
              <Button size="small" sx={{ color: '#4a6cf7', textTransform: 'none' }}>
                View All
              </Button>
            </Box>
            <List sx={{ p: 0 }}>
              {transactions.map((tx, index) => (
                <React.Fragment key={tx.id}>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar sx={{
                        bgcolor: tx.amount > 0 ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)',
                        width: 36,
                        height: 36,
                      }}>
                        {tx.amount > 0 ? <ArrowUpward sx={{ color: '#2ecc71', fontSize: 18 }} /> : <ArrowDownward sx={{ color: '#e74c3c', fontSize: 18 }} />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a2e' }}>{tx.description}</Typography>}
                      secondary={<Typography variant="caption" sx={{ color: '#8a8a9a' }}>{tx.date}</Typography>}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: tx.amount > 0 ? '#2ecc71' : '#e74c3c' }}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                      </Typography>
                      <Chip
                        label={tx.status}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '9px',
                          bgcolor: tx.status === 'completed' ? 'rgba(46, 204, 113, 0.1)' : 'rgba(243, 156, 18, 0.1)',
                          color: tx.status === 'completed' ? '#2ecc71' : '#f5a623',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  </ListItem>
                  {index < transactions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Wallet;